import * as Phaser from 'phaser';
import { loadSettings, saveSettings, type GameSettings } from '../progress';
import { LanguageDropdown } from './LanguageDropdown';
import { bindText, onLangChange } from '../i18n/helpers';
import { isRTL } from '../i18n';
import { FONT, PALETTE, TYPE } from '../render/palette';
import { addAtmosphere } from '../render/atmosphere';
import { drawBeamSegment } from '../render/beam';
import { responsiveFontSize } from './responsive-font';

const TOGGLE_W = 56;
const TOGGLE_H = 28;
const TOGGLE_KNOB_R = (TOGGLE_H - 8) / 2;
const TOGGLE_KNOB_OFFSET = TOGGLE_W / 2 - TOGGLE_H / 2;
const TOGGLE_ON_FILL = 0x3fa867; // PALETTE.G — but the rect ignores alpha so use literal.
const TOGGLE_OFF_FILL = 0x1a1a2c;
const TOGGLE_ON_STROKE = 0x3fa867;
const TOGGLE_OFF_STROKE = 0x232338;
const KNOB_OFF = 0xeeeeee;
const KNOB_ON = 0x3fa867;

export class OptionsScene extends Phaser.Scene {
  constructor() {
    super('options');
  }

  create(): void {
    addAtmosphere(this, 0.6);

    const fsTITLE = responsiveFontSize(this, 28);
    const fsLABEL = responsiveFontSize(this, 16);
    const fsHINT = responsiveFontSize(this, 13);

    const TITLE = {
      color: FONT.TITLE,
      fontFamily: TYPE.SANS,
      fontSize: `${fsTITLE}px`,
      fontStyle: '400',
    } as const;
    const LABEL = {
      color: FONT.TEXT,
      fontFamily: TYPE.SANS,
      fontSize: `${fsLABEL}px`,
      fontStyle: '500',
    } as const;
    const HINT = {
      color: FONT.DIM,
      fontFamily: TYPE.SANS,
      fontSize: `${fsHINT}px`,
    } as const;

    const titleText = this.add.text(480, 70, '', TITLE).setOrigin(0.5);
    titleText.setLetterSpacing(10);
    bindText(titleText, 'options.title', this);

    const settings = loadSettings();

    // Card-style row for the hints toggle.
    this.optionCard(
      480,
      220,
      'options.hints.label',
      'options.hints.help',
      LABEL,
      HINT,
      (rowX, rowY) => {
        const toggleX = rowX + 200;
        this.makeToggle(toggleX, rowY, settings.hintsEnabled, (value) => {
          saveSettings({ ...loadSettings(), hintsEnabled: value });
        });
      },
    );

    // Decorative beam across the bottom — pure flair.
    const flair = this.add.graphics();
    flair.setBlendMode(Phaser.BlendModes.ADD);
    flair.setAlpha(0.25);
    drawBeamSegment(flair, 120, 620, 840, 620, PALETTE.W);

    const back = this.add
      .triangle(0, 700 - 20, 0, 8, 14, 0, 14, 16, PALETTE.HUD_DIM)
      .setInteractive({ useHandCursor: true });
    back.on('pointerover', () => back.setFillStyle(PALETTE.HUD_TITLE));
    back.on('pointerout', () => back.setFillStyle(PALETTE.HUD_DIM));
    back.on('pointerdown', () => this.scene.start('menu'));

    const placeBack = (): void => {
      if (isRTL()) {
        back.x = 940;
        back.setRotation(Math.PI);
      } else {
        back.x = 20;
        back.setRotation(0);
      }
    };
    placeBack();
    onLangChange(this, placeBack);

    new LanguageDropdown(this);
  }

  /**
   * Draw a card-style option row centred on (cx, cy). The control callback
   * positions a toggle or other widget relative to the row.
   */
  private optionCard(
    cx: number,
    cy: number,
    labelKey: Parameters<typeof bindText>[1],
    helpKey: Parameters<typeof bindText>[1],
    labelStyle: Phaser.Types.GameObjects.Text.TextStyle,
    hintStyle: Phaser.Types.GameObjects.Text.TextStyle,
    placeControl: (rowX: number, rowY: number) => void,
  ): void {
    const cardW = 540;
    const cardH = 88;
    const cardLeft = cx - cardW / 2;
    const cardTop = cy - cardH / 2;

    const g = this.add.graphics();
    g.fillStyle(PALETTE.SLOT_FILL, 0.35);
    g.fillRoundedRect(cardLeft, cardTop, cardW, cardH, 6);
    g.lineStyle(1, PALETTE.HUD_TITLE, 0.08);
    g.strokeRoundedRect(cardLeft, cardTop, cardW, cardH, 6);

    const label = this.add.text(cardLeft + 24, cy - 12, '', labelStyle).setOrigin(0, 0.5);
    bindText(label, labelKey, this);

    const help = this.add.text(cardLeft + 24, cy + 14, '', hintStyle).setOrigin(0, 0.5);
    help.setWordWrapWidth(cardW - 200);
    bindText(help, helpKey, this);

    placeControl(cx, cy);
  }

  private makeToggle(
    x: number,
    y: number,
    initial: boolean,
    onChange: (value: boolean) => void,
  ): void {
    const container = this.add.container(x, y);
    // Pill background — rounded to TOGGLE_H/2 for full capsule shape.
    const bg = this.add.graphics();
    const paintBg = (on: boolean): void => {
      bg.clear();
      bg.fillStyle(on ? TOGGLE_ON_FILL : TOGGLE_OFF_FILL, on ? 0.25 : 1);
      bg.fillRoundedRect(-TOGGLE_W / 2, -TOGGLE_H / 2, TOGGLE_W, TOGGLE_H, TOGGLE_H / 2);
      bg.lineStyle(1, on ? TOGGLE_ON_STROKE : TOGGLE_OFF_STROKE, on ? 0.55 : 1);
      bg.strokeRoundedRect(-TOGGLE_W / 2, -TOGGLE_H / 2, TOGGLE_W, TOGGLE_H, TOGGLE_H / 2);
    };
    paintBg(initial);
    const knob = this.add.circle(
      initial ? TOGGLE_KNOB_OFFSET : -TOGGLE_KNOB_OFFSET,
      0,
      TOGGLE_KNOB_R,
      initial ? KNOB_ON : KNOB_OFF,
    );
    container.add([bg, knob]);
    container.setInteractive(
      new Phaser.Geom.Rectangle(-TOGGLE_W / 2, -TOGGLE_H / 2, TOGGLE_W, TOGGLE_H),
      (r: Phaser.Geom.Rectangle, px: number, py: number) =>
        Phaser.Geom.Rectangle.Contains(r, px, py),
    );
    let value = initial;
    container.on('pointerdown', () => {
      value = !value;
      paintBg(value);
      knob.setFillStyle(value ? KNOB_ON : KNOB_OFF);
      knob.x = value ? TOGGLE_KNOB_OFFSET : -TOGGLE_KNOB_OFFSET;
      onChange(value);
    });
  }
}

// Type-only export to satisfy `verbatimModuleSyntax` if a consumer wants the settings shape.
export type { GameSettings };
